import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getMemberResultForManager } from "../../services/dashboard-api";

type Result = Awaited<ReturnType<typeof getMemberResultForManager>>;

/** FR-018: a Manager's per-question breakdown of an individual employee's result within their
 * own company — 404s (surfaced as "not found" below, not a partial view) for a cross-company id,
 * per `GET /manager/members/:memberId/result`'s own contract. */
export function MemberResultPage() {
  const { slug, memberId } = useParams<{ slug: string; memberId: string }>();
  const navigate = useNavigate();
  const [result, setResult] = useState<Result | null | "loading" | "not_found">("loading");

  useEffect(() => {
    if (!memberId) return;
    getMemberResultForManager(memberId)
      .then(setResult)
      .catch(() => setResult("not_found"));
  }, [memberId]);

  if (result === "loading") return <div className="p-6 text-body">Loading…</div>;
  if (result === "not_found" || !result) return <div className="p-6 text-body">Result not found.</div>;

  return (
    <div className="mx-auto max-w-2xl p-6">
      <button onClick={() => navigate(`/company/${slug}/dashboard`)} className="mb-4 text-sm text-brand">
        ← Back to dashboard
      </button>
      <h1 className="mb-2 font-sans text-xl text-ink">Member Result</h1>
      <p className="mb-6 text-body">
        Status: {result.status}
        {result.scorePercent !== null && ` · Score: ${result.scorePercent}%`}
        {result.passed !== null && ` · ${result.passed ? "Passed" : "Not passed"}`}
      </p>

      <div className="flex flex-col gap-6">
        {result.questions.map((q) => (
          <div key={q.questionId} className="rounded border border-surface-alt p-4">
            <p className="mb-2 font-sans text-ink">{q.text}</p>
            <p className="mb-1 text-sm text-body">
              Answer: {Array.isArray(q.given) ? q.given.join(", ") : (q.given ?? "—")}
              {q.isCorrect !== null && (
                <span className={q.isCorrect ? "ml-2 text-brand" : "ml-2 text-accent"}>
                  {q.isCorrect ? "Correct" : "Incorrect"}
                </span>
              )}
            </p>
            {q.correctAnswer && <p className="text-sm text-body">Correct answer: {q.correctAnswer.join(", ")}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
